import { Link, createFileRoute } from "@tanstack/react-router";

import { FilterChips } from "~/components/filter-chips";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { Pill, StatusBadge } from "~/components/status-badge";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { CALLBACK_STATUSES } from "~/lib/enums";
import { EM_DASH } from "~/lib/format";
import { useCallbackDeliveries } from "~/lib/queries";
import { CallbackFilterBar } from "./-filter-bar";
import { NextAttemptCountdown, isStaleLease } from "./-mechanics";
import {
  ALL,
  DEFAULT_STATUS,
  hasActiveFilters,
  toFilters,
  validateCallbackSearch,
  type CallbackSearch,
} from "./-search";

export const Route = createFileRoute("/_authed/callbacks/")({
  validateSearch: validateCallbackSearch,
  component: CallbacksPage,
});

const COLUMNS = 7;

/**
 * Altura knows a payment succeeded. Did the merchant ever find out?
 *
 * GET /api/v1/callback-deliveries has existed since migration 007 but
 * self-scopes to the calling storefront, so this is the first view of the
 * delivery queue across every merchant -- the only one that separates "this
 * merchant's endpoint is down" from "ours is".
 *
 * Opens on exhausted deliveries: eight attempts, no 2xx, nothing will try
 * again. That is the state someone is being asked about.
 */
function CallbacksPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = toFilters(search);
  const { data, error, isPending, isFetching, refetch } = useCallbackDeliveries(filters);

  function patch(next: Partial<CallbackSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  function clearFilters() {
    void navigate({
      search: (previous) => ({ limit: previous.limit, status: DEFAULT_STATUS }),
      replace: true,
    });
  }

  const rows = data?.items ?? [];
  const filtered = hasActiveFilters(search);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Callbacks"
        description="Outbound merchant notifications across every storefront, and what their endpoints said back."
      />

      <CallbackFilterBar
        search={search}
        onChange={patch}
        onClear={clearFilters}
        hasFilters={filtered}
      />

      <FilterChips
        value={search.status === ALL ? undefined : search.status}
        options={CALLBACK_STATUSES}
        onChange={(next) => patch({ status: next ?? ALL })}
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Order</TableHead>
              <TableHead>Storefront</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Attempts</TableHead>
              <TableHead className="text-right">Last code</TableHead>
              <TableHead>Next attempt</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  search.status === DEFAULT_STATUS && !filtered
                    ? "No exhausted deliveries. Every merchant callback eventually got through."
                    : filtered
                      ? "No callback deliveries match these filters."
                      : "No callback deliveries yet."
                }
                onClear={filtered ? clearFilters : undefined}
              />
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Link
                      to="/callbacks/$deliveryId"
                      params={{ deliveryId: row.id }}
                      className="font-mono text-[0.8125rem] text-primary hover:underline"
                    >
                      {row.order_id}
                    </Link>
                  </TableCell>

                  <TableCell className="max-w-[12rem] truncate">
                    <Link
                      to="/storefronts/$storefrontId"
                      params={{ storefrontId: row.storefront_id }}
                      className="hover:underline"
                    >
                      {row.storefront_name}
                    </Link>
                  </TableCell>

                  <TableCell className="text-muted-foreground">{row.event_type}</TableCell>

                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      <StatusBadge status={row.status} />
                      {/* A lease older than five minutes means the worker died
                          mid-flight. Housekeeping sweeps hourly, so this row is
                          waiting, not working. */}
                      {isStaleLease(row.locked_at) ? (
                        <Pill tone="warning" title="Locked longer than the 5-minute lease">
                          stale lock
                        </Pill>
                      ) : null}
                    </span>
                  </TableCell>

                  {/* 3 / 8 rather than 3: the denominator is what tells you
                      whether there are attempts left. */}
                  <TableCell className="text-right tabular-nums">
                    <span
                      className={
                        row.attempt_count >= row.max_attempts
                          ? "text-destructive"
                          : "text-muted-foreground"
                      }
                    >
                      {row.attempt_count} / {row.max_attempts}
                    </span>
                  </TableCell>

                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.last_status_code ?? EM_DASH}
                  </TableCell>

                  <TableCell className="text-[0.8125rem]">
                    <NextAttemptCountdown at={row.next_attempt_at} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}
